import { useState } from "react"
import {
  X,
  Sparkles,
  Loader2,
  RefreshCw,
  Pencil,
  Check,
  ChevronRight,
  ChevronLeft,
  MessageSquare,
  Code2,
  Briefcase,
  Theater,
  Server,
  Plus,
} from "lucide-react"
import toast from "react-hot-toast"

// ── Question type config ──────────────────────────────────────────────────────
const QUESTION_TYPES = [
  { id: "Conversational", label: "Conversational", icon: MessageSquare },
  { id: "Coding", label: "Coding", icon: Code2 },
  { id: "Case Study", label: "Case Study", icon: Briefcase },
  { id: "Role Play", label: "Role Play", icon: Theater },
  { id: "System Design", label: "System Design", icon: Server },
]

const fetchGeneratedQuestions = async (
  questionType,
  numberOfQuestions,
  instructions,
) => {
  const response = await fetch(
    "https://hire100x-e1dd4.uc.r.appspot.com/ai-assist/generate-questions",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ questionType, numberOfQuestions, instructions }),
    },
  )
  if (!response.ok) throw new Error(`Server error: ${response.status}`)
  const data = await response.json()
  return data.questions
}

// ── Number stepper ────────────────────────────────────────────────────────────
const NumberStepper = ({ value, onChange, min = 1, max = 10 }) => (
  <div
    className="flex items-center border border-zinc-200 bg-white overflow-hidden w-20"
    style={{ borderRadius: 0 }}
  >
    <div
      className="flex-1 px-3 py-2 text-sm text-zinc-800 select-none"
      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
    >
      {String(value).padStart(2, "0")}
    </div>
    <div className="flex flex-col border-l border-zinc-200">
      <button
        onMouseDown={(e) => {
          e.preventDefault()
          if (value < max) onChange(value + 1)
        }}
        className="flex items-center justify-center w-7 h-[18px] hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors border-b border-zinc-200"
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
        >
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
      <button
        onMouseDown={(e) => {
          e.preventDefault()
          if (value > min) onChange(value - 1)
        }}
        className="flex items-center justify-center w-7 h-[18px] hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors"
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
    </div>
  </div>
)

// ── Editable question row ─────────────────────────────────────────────────────
const QuestionRow = ({
  question,
  checked,
  onToggle,
  onEdit,
}) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(question.text)

  const handleSave = () => {
    onEdit(draft)
    setEditing(false)
  }
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.metaKey) handleSave()
    if (e.key === "Escape") {
      setDraft(question.text)
      setEditing(false)
    }
  }

  return (
    <div
      className={`border transition-all mb-2 ${
        checked
          ? "border-zinc-900 bg-zinc-50"
          : "border-zinc-200 bg-white hover:border-zinc-300"
      }`}
      style={{ borderRadius: 0 }}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        {/* Checkbox */}
        <button
          onClick={onToggle}
          className={`mt-0.5 flex-shrink-0 w-4 h-4 border flex items-center justify-center transition-all ${
            checked
              ? "bg-zinc-900 border-zinc-900"
              : "bg-white border-zinc-300 hover:border-zinc-500"
          }`}
          style={{ borderRadius: 0 }}
        >
          {checked && (
            <Check size={10} strokeWidth={3} className="text-white" />
          )}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {editing ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              rows={3}
              className="w-full text-[13px] text-zinc-800 leading-relaxed border border-zinc-300 px-2 py-1.5 resize-none focus:outline-none focus:border-zinc-600 bg-white"
              style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                borderRadius: 0,
              }}
            />
          ) : (
            <p
              className="text-[13px] text-zinc-700 leading-relaxed"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              {question.text}
            </p>
          )}

          {!editing && question.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {question.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 bg-zinc-100 px-1.5 py-0.5 border border-zinc-200"
                  style={{
                    borderRadius: 0,
                    fontFamily: "'IBM Plex Mono', monospace",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {editing && (
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleSave}
                className="flex items-center gap-1 px-2.5 py-1 bg-zinc-900 text-white text-[11px] font-bold uppercase tracking-widest border border-zinc-900 hover:bg-zinc-700 transition-colors"
                style={{ borderRadius: 0 }}
              >
                <Check size={10} strokeWidth={2.5} /> Save
              </button>
              <button
                onClick={() => {
                  setDraft(question.text)
                  setEditing(false)
                }}
                className="flex items-center gap-1 px-2.5 py-1 bg-white text-zinc-500 text-[11px] font-bold uppercase tracking-widest border border-zinc-200 hover:border-zinc-400 transition-colors"
                style={{ borderRadius: 0 }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Action buttons */}
        {!editing && (
          <div className="flex items-center gap-1 flex-shrink-0 ml-1">
            <button
              onClick={() => setEditing(true)}
              title="Edit question"
              className="w-6 h-6 flex items-center justify-center border border-zinc-200 bg-white text-zinc-400 hover:border-zinc-400 hover:text-zinc-700 transition-all"
              style={{ borderRadius: 0 }}
            >
              <Pencil size={10} strokeWidth={2} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main modal ────────────────────────────────────────────────────────────────
const GenerateQuestionModal = ({ onClose, onAddQuestions }) => {
  // "config" = form screen | "results" = generated questions screen
  const [screen, setScreen] = useState("config")

  // Form state — preserved when navigating back from results
  const [selectedType, setSelectedType] = useState("Conversational")
  const [instructions, setInstructions] = useState("")
  const [numQuestions, setNumQuestions] = useState(3)

  // Results state
  const [isGenerating, setIsGenerating] = useState(false)
  const [generated, setGenerated] = useState([])
  const [checked, setChecked] = useState({})
  const [regeneratingId, setRegeneratingId] = useState(null)

  const selectedCount = Object.values(checked).filter(Boolean).length
  const allSelected =
    generated.length > 0 && generated.every((q) => checked[q.id])

 const handleGenerate = async () => {
   if (!instructions.trim()) {
     toast.error("Please add instructions before generating questions.", {
       className:
         "!font-mono !text-[11px] !rounded-none !font-medium !tracking-wide",
     })
     return
   }

   const count = selectedType === "Conversational" ? numQuestions : 1
   setIsGenerating(true)
   setGenerated([])
   setChecked({})
   setScreen("results")

   try {
     const results = await fetchGeneratedQuestions(
       selectedType,
       count,
       instructions,
     )
     const withIds = results.map((q) => ({
       ...q,
       id: Date.now() + Math.random(),
     }))
     setGenerated(withIds)
     const init = {}
     withIds.forEach((q) => {
       init[q.id] = true
     })
     setChecked(init)
   } catch (err) {
     toast.error(
       err.message || "Failed to generate questions. Please try again.",
       {
         className:
           "!font-mono !text-[11px] !rounded-none !font-medium !tracking-wide",
       },
     )
     setScreen("config")
   } finally {
     setIsGenerating(false)
   }
 }


  const handleEdit = (id, newText) =>
    setGenerated((prev) =>
      prev.map((q) => (q.id === id ? { ...q, text: newText } : q)),
    )

  const toggleAll = () => {
    const next = {}
    generated.forEach((q) => {
      next[q.id] = !allSelected
    })
    setChecked(next)
  }

  const handleAdd = () => {
    const toAdd = generated
      .filter((q) => checked[q.id])
      .map((q) => ({
        category: selectedType === "Conversational" ? null : selectedType,
        question: `<p>${q.text}</p>`,
        tags: q.tags,
        title: q.text.slice(0, 60),
      }))
    if (toAdd.length === 0) return
    onAddQuestions(toAdd)
    onClose()
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <div
          className="bg-white border border-zinc-200 shadow-2xl flex flex-col overflow-hidden"
          style={{
            borderRadius: 0,
            width: "min(720px, 96vw)",
            height: "min(620px, 92vh)",
          }}
        >
          {/* ── SCREEN 1: Config ───────────────────────────────────────────── */}
          {screen === "config" && (
            <>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 bg-white shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 bg-zinc-900 flex items-center justify-center flex-shrink-0">
                    <Sparkles size={13} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-zinc-900">
                      Generate Question
                    </p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                      Add a new skill based question to your interview
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 hover:bg-zinc-100 transition-colors text-zinc-400 hover:text-zinc-700"
                  style={{ borderRadius: 0 }}
                >
                  <X size={15} />
                </button>
              </div>

              {/* Body */}
              <div className="flex flex-col flex-1 overflow-y-auto px-6 py-6 gap-6">
                {/* Question Type */}
                <div>
                  <label className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide block mb-3">
                    Select Question Type
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {QUESTION_TYPES.map(({ id, label, icon: Icon }) => {
                      const active = selectedType === id
                      return (
                        <button
                          key={id}
                          onClick={() => setSelectedType(id)}
                          className={`flex items-center gap-2 px-3 py-2 border text-left transition-all ${
                            active
                              ? "border-blue-300 bg-blue-50 text-blue-700"
                              : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
                          }`}
                          style={{ borderRadius: 0 }}
                        >
                          <Icon
                            size={12}
                            className={
                              active ? "text-blue-400" : "text-zinc-400"
                            }
                          />
                          <span className="text-[12px] font-bold uppercase tracking-widest">
                            {label}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Number of Questions */}
                <div>
                  <label className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide block mb-2">
                    Number of Questions
                  </label>
                  <div className="flex items-center gap-3">
                    <div
                      className={
                        selectedType !== "Conversational"
                          ? "opacity-40 pointer-events-none"
                          : ""
                      }
                    >
                      <NumberStepper
                        value={
                          selectedType === "Conversational" ? numQuestions : 1
                        }
                        onChange={setNumQuestions}
                        min={1}
                        max={10}
                      />
                    </div>
                    {selectedType !== "Conversational" && (
                      <span
                        className="text-[11px] text-zinc-400"
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                      >
                        Fixed at 01 for {selectedType}
                      </span>
                    )}
                  </div>
                </div>
                {/* Instructions */}
                <div className="flex flex-col flex-1">
                  <label className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide block mb-2">
                    Instructions{" "}
                  </label>
                  <textarea
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Generate questions for Software Architecture, Distributed Systems, and API Design..."
                    rows={6}
                    className="w-full px-3 py-2.5 text-[13px] text-zinc-700 border border-zinc-200 bg-zinc-50 resize-none focus:outline-none focus:border-zinc-400 placeholder-zinc-300 leading-relaxed"
                    style={{
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      borderRadius: 0,
                    }}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 shrink-0">
                <button
                  onClick={handleGenerate}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[12px] font-bold uppercase tracking-widest border bg-zinc-900 border-zinc-900 text-white hover:bg-zinc-700 transition-all"
                  style={{ borderRadius: 0 }}
                >
                  <Sparkles size={12} />
                  Generate Questions
                </button>
              </div>
            </>
          )}

          {/* ── SCREEN 2: Results ──────────────────────────────────────────── */}
          {screen === "results" && (
            <>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 bg-white shrink-0">
                <div className="flex items-center gap-3">
                  {!isGenerating && (
                    <button
                      onClick={() => setScreen("config")}
                      className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-700 transition-colors"
                    >
                      <ChevronLeft size={14} strokeWidth={2.5} />
                      <span className="text-[11px] font-bold uppercase tracking-widest">
                        Back
                      </span>
                    </button>
                  )}
                  <div
                    className={
                      !isGenerating ? "border-l border-zinc-200 pl-3" : ""
                    }
                  >
                    <p className="text-sm font-bold text-zinc-900">
                      Generated Questions
                    </p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                      {selectedType} · {numQuestions} question
                      {numQuestions > 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {!isGenerating && generated.length > 0 && (
                    <>
                      <button
                        onClick={toggleAll}
                        className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-700 transition-colors"
                      >
                        {allSelected ? "Deselect All" : "Select All"}
                      </button>
                      <button
                        onClick={handleGenerate}
                        className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-700 transition-colors"
                      >
                        <RefreshCw size={11} strokeWidth={2} />
                        Regenerate
                      </button>
                    </>
                  )}
                  <button
                    onClick={onClose}
                    className="p-1 hover:bg-zinc-100 transition-colors text-zinc-400 hover:text-zinc-700"
                    style={{ borderRadius: 0 }}
                  >
                    <X size={15} />
                  </button>
                </div>
              </div>

              {/* Questions list */}
              <div className="flex-1 overflow-y-auto px-6 py-4 bg-zinc-50">
                {isGenerating && (
                  <div className="flex flex-col items-center justify-center h-full gap-3">
                    <Loader2 size={20} className="animate-spin text-zinc-400" />
                    <p className="text-[11px] text-zinc-400 uppercase tracking-widest font-bold">
                      Generating questions…
                    </p>
                  </div>
                )}

                {!isGenerating &&
                  generated.map((q) => (
                    <QuestionRow
                      key={q.id}
                      question={q}
                      checked={!!checked[q.id]}
                      onToggle={() =>
                        setChecked((prev) => ({ ...prev, [q.id]: !prev[q.id] }))
                      }
                      onEdit={(text) => handleEdit(q.id, text)}
                      isRegenerating={regeneratingId === q.id}
                    />
                  ))}
              </div>

              {/* Footer */}
              {!isGenerating && (
                <div className="px-6 py-4 border-t border-zinc-200 bg-white flex items-center justify-between shrink-0">
                  <p
                    className="text-[11px] text-zinc-400"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    {selectedCount} of {generated.length} selected
                  </p>
                  <button
                    onClick={handleAdd}
                    disabled={selectedCount === 0}
                    className={`flex items-center gap-2 px-5 py-2.5 text-[12px] font-bold uppercase tracking-widest border transition-all ${
                      selectedCount === 0
                        ? "bg-zinc-200 border-zinc-200 text-zinc-400 cursor-not-allowed"
                        : "bg-zinc-900 border-zinc-900 text-white hover:bg-zinc-700"
                    }`}
                    style={{ borderRadius: 0 }}
                  >
                    <Plus size={12} />
                    Add to Interview
                    <ChevronRight size={12} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default GenerateQuestionModal
